import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FileText, Clock, CheckCircle2, DollarSign, Users, Building2, CreditCard, AlertCircle, Package, Send, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    carregarStats();
    
    // Listener para atualizar em tempo real quando houver mudanças
    const handleSolicitacaoModificada = () => {
      carregarStats();
    };
    
    window.addEventListener('solicitacaoModificada', handleSolicitacaoModificada);
    
    return () => {
      window.removeEventListener('solicitacaoModificada', handleSolicitacaoModificada);
    };
  }, []);

  const carregarStats = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/stats`);
      setStats(res.data);
    } catch (error) {
      // Silenciar erros em atualizações automáticas
      if (loading) {
        toast.error('Erro ao carregar estatísticas');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const cards = [
    {
      title: 'Total de Solicitações',
      value: stats?.total_solicitacoes || 0,
      icon: FileText,
      color: 'bg-blue-500',
      testId: 'stat-total-solicitacoes',
      link: '/solicitacoes'
    },
    {
      title: 'Pendentes',
      value: stats?.pendentes || 0,
      icon: Clock,
      color: 'bg-yellow-500',
      testId: 'stat-pendentes',
      link: '/relatorios?status=Pendente'
    },
    {
      title: 'Em Tratamento',
      value: stats?.em_tratamento || 0,
      icon: AlertCircle,
      color: 'bg-orange-500',
      testId: 'stat-em-tratamento',
      link: '/relatorios?status=Em Tratamento'
    },
    {
      title: 'Montadas',
      value: stats?.montadas || 0,
      icon: Package,
      color: 'bg-purple-500',
      testId: 'stat-montadas',
      link: '/relatorios?status=Montada'
    },
    {
      title: 'Enviadas',
      value: stats?.enviadas || 0,
      icon: Send,
      color: 'bg-cyan-500',
      testId: 'stat-enviadas',
      link: '/relatorios?status=Enviada'
    },
    {
      title: 'Concluídas',
      value: stats?.concluidas || 0,
      icon: CheckCircle2,
      color: 'bg-green-500',
      testId: 'stat-concluidas',
      link: '/relatorios?status=Concluída'
    },
    {
      title: 'Financeiro',
      value: null,
      customRender: true,
      icon: DollarSign,
      color: 'bg-emerald-500',
      testId: 'stat-financeiro',
      link: '/relatorios?status=Concluída'
    },
    {
      title: 'Aguardando Pagamento',
      value: stats?.aguardando_pagamento || 0,
      icon: CreditCard,
      color: 'bg-purple-500',
      testId: 'stat-aguardando-pagamento',
      link: '/relatorios'
    },
    {
      title: 'Pagamentos Realizados',
      value: stats?.pagos || 0,
      icon: CheckCircle2,
      color: 'bg-teal-500',
      testId: 'stat-pagos',
      link: '/relatorios'
    },
    {
      title: 'Total de Clientes',
      value: stats?.total_clientes || 0,
      icon: Users,
      color: 'bg-indigo-500',
      testId: 'stat-clientes',
      link: '/clientes'
    },
    {
      title: 'Total de Fornecedores',
      value: stats?.total_fornecedores || 0,
      icon: Building2,
      color: 'bg-pink-500',
      testId: 'stat-fornecedores',
      link: '/fornecedores'
    },
  ];

  return (
    <div data-testid="dashboard-page">
      <div className="mb-6 lg:mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800 mb-2">Dashboard</h1>
        <p className="text-sm sm:text-base text-slate-600">Visão geral do sistema de reservas</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
        {cards.map((card) => {
          const Icon = card.icon;
          return (
            <Card 
              key={card.title} 
              className="hover:shadow-lg transition-all cursor-pointer active:scale-95 lg:hover:scale-105" 
              data-testid={card.testId}
              onClick={() => navigate(card.link)}
            >
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-xs sm:text-sm font-medium text-slate-600">
                  {card.title}
                </CardTitle>
                <div className={`${card.color} p-1.5 sm:p-2 rounded-lg`}>
                  <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
              </CardHeader>
              <CardContent>
                {card.customRender ? (
                  <div className="space-y-1.5 sm:space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm text-slate-600">Receita:</span>
                      <span className="text-sm sm:text-lg font-bold text-emerald-600">
                        R$ {stats?.faturamento_total?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm text-slate-600">Custo:</span>
                      <span className="text-sm sm:text-lg font-bold text-red-600">
                        R$ {stats?.custo_fornecedor?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                    <div className="h-px bg-slate-200"></div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs sm:text-sm font-semibold text-slate-700">Lucro:</span>
                      <span className={`text-base sm:text-xl font-bold ${(stats?.lucro_liquido || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        R$ {stats?.lucro_liquido?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="text-2xl sm:text-3xl font-bold text-slate-800">{card.value}</div>
                    <p className="text-xs text-slate-500 mt-1 sm:mt-2">Clique para ver detalhes</p>
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}